import React, { useEffect, useState, useRef, useMemo } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { authAPI, watchlistAPI, portfolioAPI, alertsAPI, stocksAPI, stripeAPI, intradayAPI, quotesByTicker } from '../services/api';
import { User, WatchlistItem, PortfolioPosition, Alert } from '../types';
import NavBar from '../components/NavBar';
import BackToTop from '../components/BackToTop';
import { useLivePriceContext } from '../contexts/LivePriceContext';
import MarketStatusBadge from '../components/MarketStatusBadge';
import SectorPieChart from '../components/SectorPieChart';
import { computeSectorBreakdown, useSectors, SECTOR_COLORS, SectorBreakdown } from '../utils/sectorMap';
import { summarizePositions, deriveValues } from '../utils/portfolioTotals';
import { formatDateOnly } from '../utils/dates';
import { getTierBySlug, getTierLimits, getTierSummary } from '../data/pricingTiers';
import '../styles/livePrice.css';

const EMPTY_SECTORS: SectorBreakdown[] = [
  'Technology', 'Healthcare', 'Financial Services', 'Consumer Cyclical',
  'Communication Services', 'Industrials', 'Consumer Defensive', 'Energy',
  'Real Estate', 'Utilities', 'Basic Materials',
].map(sector => ({
  sector,
  count: 0,
  value: 0,
  percentage: 0,
  color: SECTOR_COLORS[sector] || SECTOR_COLORS['Other'],
  tickers: [],
}));

interface IPOItem {
  ticker: string;
  issuer_name: string;
  listing_date: string | null;
  announced_date: string | null;
  final_issue_price: number | null;
  lowest_offer_price: number | null;
  highest_offer_price: number | null;
  total_offer_size: number | null;
  primary_exchange: string | null;
  security_type: string | null;
  security_description: string | null;
  ipo_status: string | null;
  last_updated: string | null;
  currency_code: string | null;
  min_shares_offered: number | null;
  max_shares_offered: number | null;
  // "ETF" for a fund launch, "CS" for a share IPO. Absent on older payloads.
  asset_type?: 'CS' | 'ETF' | null;
  // Populated only for the recently_active bucket
  current_price?: number | null;
  change_percent?: number | null;
  volume?: number | null;
}

type IPOTab = 'upcoming' | 'recently_active' | 'pending';

type IPOSortColumn = 'ticker' | 'company' | 'date' | 'price' | 'change';

// Asset type is a separate axis from the tabs. The tabs are a lifecycle
// (upcoming -> pending -> trading) and a fund launch has one of those states
// too, so this filters across all three rather than sitting beside them.
type IPOAssetFilter = 'CS' | 'ETF' | 'all';

const IPO_TAB_LABELS: Record<IPOTab, string> = {
  upcoming: 'Upcoming',
  recently_active: 'Recently Active',
  pending: 'Pending',
};

const IPO_ASSET_LABELS: Record<IPOAssetFilter, string> = {
  CS: 'Stocks',
  ETF: 'ETFs',
  all: 'All',
};

// What the rows are called in the empty state, per filter.
const IPO_ASSET_NOUNS: Record<IPOAssetFilter, string> = {
  CS: 'IPOs',
  ETF: 'ETF launches',
  all: 'listings',
};

interface DashboardPosition {
  ticker: string;
  quantity: number;
  buy_price: number;
  current_price?: number;
  total_value?: number;
  profit_loss?: number;
  profit_loss_percent?: number;
}

// Beginner upgrade banner copy, from the one plan source
const CASUAL = getTierBySlug('casual');
const CASUAL_SUMMARY = getTierSummary('casual');

// Portfolio card figures from summarizePositions, the rule Portfolio.tsx uses
const portfolioStats = (rows: DashboardPosition[]) => {
  const t = summarizePositions(rows);
  return { portfolioValue: t.value, portfolioPL: t.pl, portfolioPLPercent: t.plPercent, portfolioUnpriced: t.unpriced };
};

interface MarketDataItem {
  name: string;
  value: number | null;
  change?: number | null;
  changePercent?: number | null;
}

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [positions, setPositions] = useState<DashboardPosition[]>([]);
  const [watchlistTickers, setWatchlistTickers] = useState<string[]>([]);
  const [stats, setStats] = useState({
    watchlistCount: 0,
    portfolioCount: 0,
    alertsCount: 0,
    portfolioValue: 0,
    portfolioPL: 0,
    portfolioPLPercent: 0,
    portfolioUnpriced: 0,
  });

  // Live price context
  const { prices, isConnected, subscribe, unsubscribe } = useLivePriceContext();
  const previousPricesRef = useRef<Map<string, number>>(new Map());
  // const [valueFlash, setValueFlash] = useState<'green' | 'red' | null>(null);

  // IPO data
  const [ipoData, setIpoData] = useState<Record<IPOTab, IPOItem[]>>({ upcoming: [], recently_active: [], pending: [] });
  const [ipoLoading, setIpoLoading] = useState(false);
  const [ipoTab, setIpoTab] = useState<IPOTab>('upcoming');
  // Defaults to stocks: new ETF listings run several times the rate of share
  // IPOs, so an unfiltered tracker reads as a fund-launch feed.
  const [ipoAssetFilter, setIpoAssetFilter] = useState<IPOAssetFilter>('CS');

  // IPO column sorting. Null column = the default listing-date order below.
  const [ipoSortColumn, setIpoSortColumn] = useState<IPOSortColumn | null>(null);
  const [ipoSortDirection, setIpoSortDirection] = useState<'asc' | 'desc'>('asc');

  const handleIpoSort = (column: IPOSortColumn) => {
    if (ipoSortColumn === column) {
      setIpoSortDirection(prev => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setIpoSortColumn(column);
      // Text reads A→Z first; dates and numbers read newest/largest first.
      setIpoSortDirection(column === 'ticker' || column === 'company' ? 'asc' : 'desc');
    }
  };

  // Every tab, asset-filtered. Drives the tab counts as well as the table, so
  // a badge never promises rows the filter is about to hide. An item with no
  // asset_type counts as a stock, matching the backend's fail-open default.
  const visibleIpos = useMemo<Record<IPOTab, IPOItem[]>>(() => {
    const keep = (ipo: IPOItem) =>
      ipoAssetFilter === 'all' || (ipo.asset_type ?? 'CS') === ipoAssetFilter;
    return {
      upcoming: ipoData.upcoming.filter(keep),
      recently_active: ipoData.recently_active.filter(keep),
      pending: ipoData.pending.filter(keep),
    };
  }, [ipoData, ipoAssetFilter]);

  const sortedIpos = useMemo(() => {
    const rows = [...visibleIpos[ipoTab]];

    if (!ipoSortColumn) {
      // Default: soonest-first while upcoming, newest-first once listed.
      return rows.sort((a, b) => {
        if (!a.listing_date) return 1;
        if (!b.listing_date) return -1;
        return ipoTab === 'upcoming'
          ? a.listing_date.localeCompare(b.listing_date)
          : b.listing_date.localeCompare(a.listing_date);
      });
    }

    // The last two columns change meaning per tab — they show the offer range and
    // offer size outside recently_active, matching the header labels.
    const valueOf = (ipo: IPOItem): string | number | null => {
      switch (ipoSortColumn) {
        case 'ticker':
          return ipo.ticker && ipo.ticker !== 'N/A' ? ipo.ticker : null;
        case 'company':
          return ipo.issuer_name || null;
        case 'date':
          return ipo.listing_date;
        case 'price':
          return ipoTab === 'recently_active'
            ? ipo.current_price ?? null
            : ipo.lowest_offer_price ?? null;
        case 'change':
          return ipoTab === 'recently_active'
            ? ipo.change_percent ?? null
            : ipo.total_offer_size ?? null;
        default:
          return null;
      }
    };

    const dir = ipoSortDirection === 'asc' ? 1 : -1;
    return rows.sort((a, b) => {
      const aVal = valueOf(a);
      const bVal = valueOf(b);
      // Missing values sink to the bottom in both directions.
      if (aVal === null && bVal === null) return 0;
      if (aVal === null) return 1;
      if (bVal === null) return -1;
      if (typeof aVal === 'string' && typeof bVal === 'string') {
        return aVal.localeCompare(bVal) * dir;
      }
      return ((aVal as number) - (bVal as number)) * dir;
    });
  }, [visibleIpos, ipoTab, ipoSortColumn, ipoSortDirection]);

  // IPO detail modal
  const [ipoModalOpen, setIpoModalOpen] = useState(false);
  const [ipoModalTicker, setIpoModalTicker] = useState<string>('');
  const [ipoModalIpo, setIpoModalIpo] = useState<IPOItem | null>(null);
  const [ipoModalQuote, setIpoModalQuote] = useState<any>(null);
  const [ipoModalCompany, setIpoModalCompany] = useState<any>(null);
  const [ipoModalNews, setIpoModalNews] = useState<any[]>([]);
  const [ipoModalLoading, setIpoModalLoading] = useState(false);

  // Trial countdown
  const [trialEnd, setTrialEnd] = useState<number | null>(null); // Unix timestamp

  // Market overview cards
  const [treasuryRates, setTreasuryRates] = useState<MarketDataItem[]>([]);
  const [treasuryDate, setTreasuryDate] = useState<string | null>(null);
  const [commodities, setCommodities] = useState<MarketDataItem[]>([]);
  const [indexes, setIndexes] = useState<MarketDataItem[]>([]);
  const [crypto, setCrypto] = useState<MarketDataItem[]>([]);
  const [marketDataLoading, setMarketDataLoading] = useState(false);
  const [marketModal, setMarketModal] = useState<'treasury' | 'commodities' | 'indexes' | 'crypto' | null>(null);

  // AI portfolio analysis — persisted in sessionStorage so it survives navigation
  const [aiSummary, setAiSummary] = useState<string | null>(() => {
    try { return sessionStorage.getItem('nwc_ai_summary'); } catch { return null; }
  });
  const [aiGeneratedAt, setAiGeneratedAt] = useState<string | null>(() => {
    try { return sessionStorage.getItem('nwc_ai_summary_at'); } catch { return null; }
  });
  const [aiLoading, setAiLoading] = useState(false);

  // Sector breakdowns for pie charts. useSectors resolves labels from the backend
  // so the charts agree with the Portfolio/Watchlist tables; its identity changes
  // once hydration lands, which recomputes both breakdowns.
  const resolveSector = useSectors(
    useMemo(
      () => [...positions.map(p => p.ticker), ...watchlistTickers],
      [positions, watchlistTickers]
    )
  );

  const portfolioSectors = useMemo(
    () => computeSectorBreakdown(
      positions.map(p => ({ ticker: p.ticker, value: p.total_value || (p.current_price || p.buy_price) * p.quantity })),
      resolveSector
    ),
    [positions, resolveSector]
  );

  const watchlistSectors = useMemo(
    () => computeSectorBreakdown(
      watchlistTickers.map(ticker => ({ ticker })),
      resolveSector
    ),
    [watchlistTickers, resolveSector]
  );

  useEffect(() => {
    loadDashboardData();
    try {
      sessionStorage.removeItem('nwc_active_sector');
      // Clear any cached sector snapshots so a fresh sector click fetches new data
      Object.keys(sessionStorage).forEach(key => {
        if (key.startsWith('nwc_sector_snapshots_')) sessionStorage.removeItem(key);
      });
    } catch {}
  }, [location.pathname]);

  // Derive a stable ticker string so we only re-subscribe when actual tickers change
  const tickerList = useMemo(
    () => positions.map(pos => pos.ticker).sort().join(','),
    [positions]
  );

  // Subscribe to portfolio tickers for live prices. Not gated on isConnected:
  // subscribe() is what opens the socket when it is down, and it resubscribes on open.
  useEffect(() => {
    if (tickerList) {
      const tickers = tickerList.split(',');
      subscribe(tickers);
      return () => {
        unsubscribe(tickers);
      };
    }
  }, [tickerList, subscribe, unsubscribe]);

  // Track previous portfolio value for flash animation (avoids stale closure on stats)
  const prevPortfolioValueRef = useRef<number>(0);

  // Update positions and dashboard stats with live prices
  // Uses functional updater to avoid stale closure on positions
  useEffect(() => {
    if (prices.size === 0) return;

    setPositions(prevPositions => {
      if (prevPositions.length === 0) return prevPositions;

      let hasChange = false;

      const updatedPositions = prevPositions.map(pos => {
        const livePrice = prices.get(pos.ticker);
        if (!livePrice) return pos;

        const previousPrice = previousPricesRef.current.get(pos.ticker) || pos.current_price || livePrice.price;

        if (livePrice.price !== previousPrice) {
          hasChange = true;
          previousPricesRef.current.set(pos.ticker, livePrice.price);

          return { ...pos, ...deriveValues(pos, livePrice.price) };
        }
        return pos;
      });

      if (hasChange) {
        const totals = portfolioStats(updatedPositions);

        // // Flash animation on the portfolio value card
        // const prevTotalValue = prevPortfolioValueRef.current;
        // if (totalValue !== prevTotalValue) {
        //   setValueFlash(totalValue > prevTotalValue ? 'green' : 'red');
        //   //setTimeout(() => setValueFlash(null), 600);
        // }
        // prevPortfolioValueRef.current = totalValue;

        setStats(prev => ({ ...prev, ...totals }));

        return updatedPositions;
      }

      return prevPositions;
    });
  }, [prices]);

  // Auto-refresh prices every 30 seconds via REST (matches Portfolio.tsx behavior)
  useEffect(() => {
    if (!tickerList) return;

    const refreshPrices = async () => {
      try {
        const priceResponse = await intradayAPI.getBatch(tickerList.split(','));
        const quotes = quotesByTicker(priceResponse.data);

        setPositions(prev => {
          const updated = prev.map(pos => {
            const freshPrice = quotes[pos.ticker]?.price;
            if (!freshPrice || freshPrice === pos.current_price) return pos;
            return { ...pos, ...deriveValues(pos, freshPrice) };
          });

          const totals = portfolioStats(updated);
          setStats(s => ({ ...s, ...totals }));
          prevPortfolioValueRef.current = totals.portfolioValue;

          return updated;
        });
      } catch {
        // Silent fail — WebSocket and initial load are primary
      }
    };

    const interval = setInterval(refreshPrices, 30000);
    return () => clearInterval(interval);
  }, [tickerList]);

  // Auto-refresh market overview cards every 30 seconds (silent — no loading spinner)
  useEffect(() => {
    const interval = setInterval(() => loadMarketOverview(true), 30000);
    return () => clearInterval(interval);
  }, []);

  const loadDashboardData = async () => {
    try {
      // Get current user first (needed for auth check)
      const userResponse = await authAPI.getCurrentUser();
      setUser(userResponse.data);

      // Fetch watchlist, portfolio, and alerts in parallel
      const [watchlistResponse, portfolioResponse, alertsResponse] = await Promise.all([
        watchlistAPI.getAll(),
        portfolioAPI.getAll(),
        alertsAPI.getAll(),
      ]);

      // Process watchlist
      const watchlistItems = watchlistResponse.data.items || [];
      const watchlistCount = watchlistItems.length;
      setWatchlistTickers(watchlistItems.map((item: any) => item.ticker));

      // Process portfolio
      const portfolioData = portfolioResponse.data;
      const portfolioPositions: DashboardPosition[] = (portfolioData.positions || []).map((p: any) => ({
        ticker: p.ticker,
        quantity: p.quantity,
        buy_price: p.buy_price,
        current_price: p.current_price,
        total_value: p.total_value,
        profit_loss: p.profit_loss,
        profit_loss_percent: p.profit_loss_percent,
      }));
      const portfolioCount = portfolioPositions.length;

      // Process alerts
      const alertsCount = alertsResponse.data.alerts?.filter((a: Alert) => a.is_active).length || 0;

      // Fetch fresh intraday prices (same as Portfolio.tsx) to override stale prev-day close
      let finalPositions = portfolioPositions;

      if (portfolioPositions.length > 0) {
        try {
          const priceResponse = await intradayAPI.getBatch(portfolioPositions.map(p => p.ticker));
          const quotes = quotesByTicker(priceResponse.data);

          // Override with fresh prices; a position with neither price stays unpriced
          finalPositions = portfolioPositions.map(pos => ({ ...pos, ...deriveValues(pos, quotes[pos.ticker]?.price) }));
        } catch (priceErr) {
          console.warn('⚠️ Could not fetch fresh intraday prices for dashboard, using cached:', priceErr);
        }
      }

      const totals = portfolioStats(finalPositions);
      setPositions(finalPositions);
      prevPortfolioValueRef.current = totals.portfolioValue;
      setStats({
        watchlistCount,
        portfolioCount,
        alertsCount,
        ...totals,
      });

      // IPO is non-critical — load without blocking render
      loadIPOData();

      // Market overview cards — non-blocking
      loadMarketOverview();

      // Trial status — non-blocking
      loadTrialStatus();
    } catch (error) {
      console.error('Failed to load dashboard data:', error);
      // If unauthorized, redirect to login
      if ((error as any).response?.status === 401) {
        localStorage.removeItem('access_token');
        navigate('/login');
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  // Non-blocking IPO loader
  const loadIPOData = async () => {
    try {
      setIpoLoading(true);
      const ipoResponse = await stocksAPI.getIpos();
      setIpoData({
        upcoming: ipoResponse.data?.upcoming ?? [],
        recently_active: ipoResponse.data?.recently_active ?? [],
        pending: ipoResponse.data?.pending ?? [],
      });
    } catch (ipoErr) {
      console.warn('Could not fetch IPO data:', ipoErr);
    } finally {
      setIpoLoading(false);
    }
  };

  // Non-blocking market overview loader (Treasury, Commodities, Indexes, Crypto)
  const loadMarketOverview = async (silent: boolean = false) => {
    if (!silent) setMarketDataLoading(true);
    try {
      // Treasury Rates
      try {
        const treasuryResp = await stocksAPI.getTreasuryRates();
        const rates = treasuryResp.data?.rates || [];
        setTreasuryDate(treasuryResp.data?.date ?? null);
        setTreasuryRates(rates.map((r: any) => ({
          name: r.name,
          value: r.value,
          change: r.change,
          changePercent: r.changePercent,
        })));
      } catch (err) {
        console.warn('Could not fetch treasury rates:', err);
      }

      // Commodities
      try {
        const commodityResp = await stocksAPI.getCommodityQuotes();
        const items = commodityResp.data?.commodities || [];
        setCommodities(items.map((c: any) => ({
          name: c.name,
          value: c.value,
          change: c.change,
          changePercent: c.changePercent,
        })));
      } catch (err) {
        console.warn('Could not fetch commodity quotes:', err);
      }

      // Indexes
      try {
        const indexResp = await stocksAPI.getIndexQuotes();
        const indexItems = indexResp.data?.indexes || [];
        setIndexes(indexItems.map((i: any) => ({
          name: i.name,
          value: i.value,
          change: i.change,
          changePercent: i.changePercent,
        })));
      } catch (err) {
        console.warn('Could not fetch index quotes:', err);
      }

      // Crypto
      try {
        const cryptoResp = await stocksAPI.getCryptoQuotes();
        const cryptoItems = cryptoResp.data?.cryptos || [];
        setCrypto(cryptoItems.map((c: any) => ({
          name: c.name,
          value: c.value,
          change: c.change,
          changePercent: c.changePercent,
        })));
      } catch (err) {
        console.warn('Could not fetch crypto quotes:', err);
      }
    } catch (err) {
      console.warn('Could not fetch market overview:', err);
    } finally {
      if (!silent) setMarketDataLoading(false);
    }
  };

  // Non-blocking trial status loader
  const loadTrialStatus = async () => {
    try {
      const res = await stripeAPI.getSubscriptionStatus();
      if (res.data.trial_end) {
        setTrialEnd(res.data.trial_end);
      }
    } catch (err) {
      // Non-fatal
    }
  };

  // Compute trial days remaining
  const getTrialDaysRemaining = (): number | null => {
    if (!trialEnd) return null;
    const now = Math.floor(Date.now() / 1000);
    const remaining = trialEnd - now;
    if (remaining <= 0) return 0;
    return Math.ceil(remaining / 86400);
  };

  const trialDaysRemaining = getTrialDaysRemaining();

  // IPO detail modal — fetch quote, company, and news for a ticker
  const openIpoModal = async (ipo: IPOItem) => {
    setIpoModalIpo(ipo);
    setIpoModalTicker(ipo.ticker);
    setIpoModalOpen(true);
    setIpoModalLoading(true);
    setIpoModalQuote(null);
    setIpoModalCompany(null);
    setIpoModalNews([]);
    // Pre-check watchlist status
    setIpoWatchlistStatus(
      watchlistTickers.includes(ipo.ticker.toUpperCase()) ? 'already' : 'idle'
    );

    // Fetch all three in parallel — each is non-fatal
    const [quoteRes, companyRes, newsRes] = await Promise.allSettled([
      stocksAPI.getQuote(ipo.ticker),
      stocksAPI.getCompany(ipo.ticker),
      stocksAPI.getNews(ipo.ticker, 5),
    ]);

    if (quoteRes.status === 'fulfilled') setIpoModalQuote(quoteRes.value.data);
    if (companyRes.status === 'fulfilled') setIpoModalCompany(companyRes.value.data);
    if (newsRes.status === 'fulfilled') setIpoModalNews(newsRes.value.data?.data || []);

    setIpoModalLoading(false);
  };

  const closeIpoModal = () => {
    setIpoModalOpen(false);
    setIpoModalTicker('');
    setIpoModalIpo(null);
    setIpoWatchlistStatus('idle');
  };

  const [ipoWatchlistStatus, setIpoWatchlistStatus] = useState<'idle' | 'adding' | 'added' | 'already' | 'error'>('idle');

  const handleIpoAddToWatchlist = async () => {
    if (!ipoModalTicker || ipoModalTicker === 'N/A') return;

    // Check if already on watchlist
    if (watchlistTickers.includes(ipoModalTicker.toUpperCase())) {
      setIpoWatchlistStatus('already');
      return;
    }

    setIpoWatchlistStatus('adding');
    try {
      await watchlistAPI.add({ ticker: ipoModalTicker, notes: `IPO — ${ipoModalIpo?.issuer_name || ''}` });
      setIpoWatchlistStatus('added');
      // Update local watchlist state so the check works immediately
      setWatchlistTickers(prev => [...prev, ipoModalTicker.toUpperCase()]);
      setStats(prev => ({ ...prev, watchlistCount: prev.watchlistCount + 1 }));
    } catch (err: any) {
      // Backend may return 400 if duplicate
      if (err.response?.status === 400) {
        setIpoWatchlistStatus('already');
      } else {
        setIpoWatchlistStatus('error');
      }
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await loadDashboardData();
  };

  const handleLogout = () => {
    localStorage.removeItem('access_token');
    try {
      sessionStorage.removeItem('nwc_ai_summary');
      sessionStorage.removeItem('nwc_ai_summary_at');
    } catch {}
    navigate('/');
  };

  const handleSectorClick = (sector: string) => {
    navigate(`/stocks?sector=${encodeURIComponent(sector)}`);
  };

  const handlePortfolioSectorClick = (sector: string) => {
    navigate(`/portfolio?sector=${encodeURIComponent(sector)}`);
  };

  const handleAnalyzePortfolio = async () => {
    setAiLoading(true);
    try {
      const response = await portfolioAPI.analyze();
      const summary = response.data.summary;
      const generatedAt = response.data.generated_at;
      setAiSummary(summary);
      setAiGeneratedAt(generatedAt);
      try {
        sessionStorage.setItem('nwc_ai_summary', summary);
        sessionStorage.setItem('nwc_ai_summary_at', generatedAt);
      } catch {}
    } catch (err: any) {
      const detail = err?.response?.data?.detail;
      if (err?.response?.status === 403) {
        const msg = typeof detail === 'object' ? detail.message : detail;
        setAiSummary(msg || 'AI Portfolio Analysis is not available on your current plan.');
      } else {
        setAiSummary('Unable to generate analysis at this time. Please try again.');
      }
    } finally {
      setAiLoading(false);
    }
  };


  const tierLimits = getTierLimits(user?.subscription_tier);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-800">
      {/* SEO — React 19 hoists to <head> */}
      <title>Dashboard — NWC-Analytics</title>
      <meta name="description" content="Your live market dashboard — watchlist prices, market overview, and alerts." />
      <link rel="canonical" href="https://nwc-analytics.com/dashboard" />
      {/* Navigation */}
      <NavBar currentPage="dashboard" user={user} onLogout={handleLogout} />

      {/* Trial Countdown Notice */}
      {trialDaysRemaining !== null && trialDaysRemaining > 0 && (
        <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8 pt-3">
          <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
            <svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span>
              {trialDaysRemaining === 1
                ? 'Trial period ends tomorrow'
                : `Trial period ends in ${trialDaysRemaining} days`}
            </span>
          </div>
        </div>
      )}

    {/* Main Content */}
    <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Welcome Section with Refresh Button & Market Status */}
      <div className="mb-8 flex justify-between items-start">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
              Welcome back, {user?.full_name || 'Investor'}!
            </h1>
            <MarketStatusBadge isConnected={isConnected} />
          </div>
          <p className="text-gray-600 dark:text-gray-400 mt-1">Here's your portfolio overview</p>
        </div>
        <button
          onClick={handleRefresh}
          disabled={refreshing}
          className="flex items-center gap-2 px-4 py-2 bg-primary-600 hover:bg-primary-700 dark:bg-primary-500 dark:hover:bg-primary-600 text-white rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <svg 
            className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} 
            fill="none" 
            viewBox="0 0 24 24" 
            stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
          {refreshing ? 'Refreshing...' : 'Refresh'}
        </button>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        {/* Portfolio Value Card — flashes on live update */}
        <div className="bg-white dark:bg-gray-700 rounded-lg shadow dark:shadow-gray-200/20 p-4 border dark:border-gray-500">
        
        {/* Flash animation on Portfolio Card 
        <div
          className={`bg-white dark:bg-gray-700 rounded-lg shadow dark:shadow-gray-200/20 p-4 border dark:border-gray-500 price-transition ${
            valueFlash === 'green'
              ? 'ring-2 ring-green-400'
              : valueFlash === 'red'
              ? 'ring-2 ring-red-400'
              : ''
          }`}
          style={
            valueFlash === 'green'
              ? { animation: 'price-flash-green 0.6s ease-in-out' }
              : valueFlash === 'red'
              ? { animation: 'price-flash-red 0.6s ease-in-out' }
              : undefined
          }
        > */}
          <div className="flex items-center justify-between">
            <div className="min-w-0">
              <p className="text-xs text-gray-600 dark:text-gray-400 mb-0.5">Portfolio Value</p>
              <p className="text-xl font-bold text-gray-900 dark:text-white truncate">
                ${stats.portfolioValue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </p>
              <p className={`text-xs font-medium mt-0.5 ${stats.portfolioPL >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                {stats.portfolioPL >= 0 ? '+' : ''}{stats.portfolioPL.toFixed(2)} ({stats.portfolioPLPercent >= 0 ? '+' : ''}{stats.portfolioPLPercent.toFixed(2)}%)
              </p>
              {stats.portfolioUnpriced > 0 && (
                <p
                  className="text-xs mt-0.5 text-amber-600 dark:text-amber-400"
                  title="No current price for these positions, so they are left out of the value and P&L"
                >
                  {stats.portfolioUnpriced} unpriced
                </p>
              )}
            </div>
            <div className="bg-green-100 dark:bg-green-900/30 rounded-lg p-2 ml-2 flex-shrink-0">
              <svg className="w-5 h-5 text-green-600 dark:text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-700 rounded-lg shadow dark:shadow-gray-200/20 p-4 border dark:border-gray-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-gray-600 dark:text-gray-400 mb-0.5">Watchlist</p>
              <p className="text-xl font-bold text-gray-900 dark:text-white">{stats.watchlistCount}</p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">of {tierLimits.watchlist} stocks</p>
            </div>
            <div className="bg-primary-100 dark:bg-primary-900/30 rounded-lg p-2 ml-2 flex-shrink-0">
              <svg className="w-5 h-5 text-primary-600 dark:text-primary-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
              </svg>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-700 rounded-lg shadow dark:shadow-gray-200/20 p-4 border dark:border-gray-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-gray-600 dark:text-gray-400 mb-0.5">Positions</p>
              <p className="text-xl font-bold text-gray-900 dark:text-white">{stats.portfolioCount}</p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">of {tierLimits.portfolio} positions</p>
            </div>
            <div className="bg-purple-100 dark:bg-purple-900/30 rounded-lg p-2 ml-2 flex-shrink-0">
              <svg className="w-5 h-5 text-purple-600 dark:text-purple-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-700 rounded-lg shadow dark:shadow-gray-200/20 p-4 border dark:border-gray-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-gray-600 dark:text-gray-400 mb-0.5">Active Alerts</p>
              <p className="text-xl font-bold text-gray-900 dark:text-white">{stats.alertsCount}</p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">monitoring</p>
            </div>
            <div className="bg-yellow-100 dark:bg-yellow-900/30 rounded-lg p-2 ml-2 flex-shrink-0">
              <svg className="w-5 h-5 text-yellow-600 dark:text-yellow-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
              </svg>
            </div>
          </div>
        </div>
      </div>

      {/* AI Portfolio Analysis */}
      <div className="mb-6">
        {positions.length === 0 ? (
          <div className="flex items-center gap-3 px-4 py-3 bg-gray-100 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 rounded-lg text-sm text-gray-500 dark:text-gray-400">
            <span className="text-base">✦</span>
            <span>Add positions to your portfolio to get AI analysis.</span>
          </div>
        ) : (
          <>
            <div className="flex items-center gap-3">
              <button
                onClick={handleAnalyzePortfolio}
                disabled={aiLoading}
                className="flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 dark:bg-indigo-500 dark:hover:bg-indigo-600 text-white rounded-lg font-medium text-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {aiLoading ? (
                  <>
                    <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                    Analyzing your portfolio...
                  </>
                ) : (
                  <>
                    <span className="text-base leading-none">✦</span>
                    Analyze Portfolio
                  </>
                )}
              </button>
            </div>

            {aiSummary && (
              <div className="mt-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-indigo-600 dark:text-indigo-400 text-base">✦</span>
                      <span className="text-xs font-semibold text-indigo-600 dark:text-indigo-400 uppercase tracking-wide">AI-generated summary</span>
                      {aiGeneratedAt && (
                        <span className="text-xs text-gray-400 dark:text-gray-500">
                          · {new Date(aiGeneratedAt).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed">{aiSummary}</p>
                  </div>
                  <button
                    onClick={handleAnalyzePortfolio}
                    disabled={aiLoading}
                    className="flex-shrink-0 text-xs text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 dark:hover:text-indigo-300 font-medium disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                  >
                    Refresh
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Market Overview Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {/* Treasury Rates Card */}
        <button
          onClick={() => setMarketModal('treasury')}
          className="bg-white dark:bg-gray-700 rounded-lg shadow dark:shadow-gray-200/20 p-4 border dark:border-gray-500 hover:border-blue-400 dark:hover:border-blue-500 transition-colors text-left group"
        >
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <div className="bg-blue-100 dark:bg-blue-900/30 rounded-lg p-1.5">
                <svg className="w-4 h-4 text-blue-600 dark:text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
              </div>
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Treasury</h3>
            </div>
            <svg className="w-4 h-4 text-gray-400 group-hover:text-blue-500 transition-colors" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </div>
          {marketDataLoading ? (
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mx-auto mt-2"></div>
          ) : treasuryRates.length > 0 ? (
            <div className="space-y-1">
              {treasuryRates.filter(r => ['2 Year', '10 Year', '30 Year'].includes(r.name)).map((item) => (
                <div key={item.name} className="flex items-center justify-between">
                  <span className="text-[0.65rem] text-gray-500 dark:text-gray-400">{item.name}</span>
                  <div className="flex items-center gap-1.5">
                    <span className="text-xs font-semibold text-gray-900 dark:text-white">{item.value != null ? `${item.value.toFixed(2)}%` : '—'}</span>
                    {item.change != null && (
                      <span className={`text-[0.6rem] font-medium ${item.change >= 0 ? 'text-green-500 dark:text-green-400' : 'text-red-500 dark:text-red-400'}`}>
                        {item.change >= 0 ? '▲' : '▼'}{Math.abs(item.change).toFixed(3)}
                      </span>
                    )}
                  </div>
                </div>
              ))}
              {treasuryDate && (
                <p className="text-[0.6rem] text-gray-400 dark:text-gray-500 pt-0.5">Close of {formatDateOnly(treasuryDate, { month: 'short', day: 'numeric' })}</p>
              )}
            </div>
          ) : (
            <p className="text-xs text-gray-400 dark:text-gray-500 mt-2">Click to view</p>
          )}
        </button>

        {/* Commodities Card */}
        <button
          onClick={() => setMarketModal('commodities')}
          className="bg-white dark:bg-gray-700 rounded-lg shadow dark:shadow-gray-200/20 p-4 border dark:border-gray-500 hover:border-yellow-400 dark:hover:border-yellow-500 transition-colors text-left group"
        >
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <div className="bg-yellow-100 dark:bg-yellow-900/30 rounded-lg p-1.5">
                <svg className="w-4 h-4 text-yellow-600 dark:text-yellow-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                </svg>
              </div>
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Commodities</h3>
            </div>
            <svg className="w-4 h-4 text-gray-400 group-hover:text-yellow-500 transition-colors" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </div>
          {marketDataLoading ? (
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-yellow-600 mx-auto mt-2"></div>
          ) : commodities.length > 0 ? (
            <div className="space-y-1">
              {commodities.slice(0, 3).map((item) => (
                <div key={item.name} className="flex items-center justify-between">
                  <span className="text-[0.65rem] text-gray-500 dark:text-gray-400">{item.name}</span>
                  <span className="text-xs font-semibold text-gray-900 dark:text-white">{item.value != null ? `$${item.value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '—'}</span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-xs text-gray-400 dark:text-gray-500 mt-2">Click to view</p>
          )}
        </button>

        {/* Indexes Card */}
        <button
          onClick={() => setMarketModal('indexes')}
          className="bg-white dark:bg-gray-700 rounded-lg shadow dark:shadow-gray-200/20 p-4 border dark:border-gray-500 hover:border-green-400 dark:hover:border-green-500 transition-colors text-left group"
        >
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <div className="bg-green-100 dark:bg-green-900/30 rounded-lg p-1.5">
                <svg className="w-4 h-4 text-green-600 dark:text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                </svg>
              </div>
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Indexes</h3>
            </div>
            <svg className="w-4 h-4 text-gray-400 group-hover:text-green-500 transition-colors" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </div>
          {marketDataLoading ? (
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-green-600 mx-auto mt-2"></div>
          ) : indexes.length > 0 ? (
            <div className="space-y-1">
              {indexes.slice(0, 3).map((item) => (
                <div key={item.name} className="flex items-center justify-between">
                  <span className="text-[0.65rem] text-gray-500 dark:text-gray-400">{item.name}</span>
                  <span className="text-xs font-semibold text-gray-900 dark:text-white">{item.value != null ? item.value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '—'}</span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-xs text-gray-400 dark:text-gray-500 mt-2">Click to view</p>
          )}
        </button>

        {/* Cryptocurrency Card */}
        <button
          onClick={() => setMarketModal('crypto')}
          className="bg-white dark:bg-gray-700 rounded-lg shadow dark:shadow-gray-200/20 p-4 border dark:border-gray-500 hover:border-orange-400 dark:hover:border-orange-500 transition-colors text-left group"
        >
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <div className="bg-orange-100 dark:bg-orange-900/30 rounded-lg p-1.5">
                <svg className="w-4 h-4 text-orange-600 dark:text-orange-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Crypto</h3>
            </div>
            <svg className="w-4 h-4 text-gray-400 group-hover:text-orange-500 transition-colors" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </div>
          {marketDataLoading ? (
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-orange-600 mx-auto mt-2"></div>
          ) : crypto.length > 0 ? (
            <div className="space-y-1">
              {crypto.slice(0, 3).map((item) => (
                <div key={item.name} className="flex items-center justify-between">
                  <span className="text-[0.65rem] text-gray-500 dark:text-gray-400">{item.name}</span>
                  <span className="text-xs font-semibold text-gray-900 dark:text-white">{item.value != null ? `$${item.value.toLocaleString('en-US', { maximumFractionDigits: 0 })}` : '—'}</span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-xs text-gray-400 dark:text-gray-500 mt-2">Click to view</p>
          )}
        </button>
      </div>

      {/* Recent Activity / Empty State */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mt-8">
        {/* Portfolio Summary */}
        <div className="bg-white dark:bg-gray-700 rounded-lg shadow-lg dark:shadow-gray-200/20 p-6 border dark:border-gray-500">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Portfolio Overview</h2>
            <Link to="/portfolio" className="text-sm text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 font-medium">
              View all →
            </Link>
          </div>
          {stats.portfolioCount > 0 ? (
            <div className="space-y-4">
              <p className="text-sm text-gray-600 dark:text-gray-400">{stats.portfolioCount} position(s) tracked</p>
              {portfolioSectors.length > 0 && (
                <div className="pt-2">
                  <SectorPieChart
                    data={portfolioSectors}
                    title="Sector Allocation"
                    mode="value"
                    size={140}
                    onSectorClick={handlePortfolioSectorClick}
                  />
                </div>
              )}
              <div className="border-t dark:border-gray-600 pt-3">
                <Link 
                  to="/portfolio"
                  className="text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 text-sm font-medium"
                >
                  Manage your positions →
                </Link>
              </div>
            </div>
          ) : (
            <div className="text-center py-8">
              <div className="bg-gray-100 dark:bg-gray-700 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-gray-400 dark:text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
              </div>
              <p className="text-gray-600 dark:text-gray-400 mb-4">No portfolio positions yet</p>
              <Link
                to="/portfolio"
                className="inline-block bg-primary-600 hover:bg-primary-700 dark:bg-primary-500 dark:hover:bg-primary-600 text-white px-4 py-2 rounded-lg font-medium transition-colors"
              >
                Add Your First Position
              </Link>
            </div>
          )}
        </div>

        {/* Watchlist Summary */}
        <div className="bg-white dark:bg-gray-700 rounded-lg shadow-lg dark:shadow-gray-200/20 p-6 border dark:border-gray-500">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Watchlist Overview</h2>
            <Link to="/watchlist" className="text-sm text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 font-medium">
              View all →
            </Link>
          </div>
          {stats.watchlistCount > 0 ? (
            <div className="space-y-4">
              <p className="text-sm text-gray-600 dark:text-gray-400">{stats.watchlistCount} stock(s) watched</p>
              {watchlistSectors.length > 0 && (
                <div className="pt-2">
                  <SectorPieChart
                    data={watchlistSectors}
                    title="Sector Breakdown"
                    mode="count"
                    size={140}
                    onSectorClick={handleSectorClick}
                  />
                </div>
              )}
              <div className="border-t dark:border-gray-600 pt-3">
                <Link 
                  to="/watchlist"
                  className="text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 text-sm font-medium"
                >
                  View your watchlist →
                </Link>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <p className="text-sm text-gray-600 dark:text-gray-400">Click a sector to find stocks</p>
              <div className="pt-2">
                <SectorPieChart
                  data={EMPTY_SECTORS}
                  title="Sector Breakdown"
                  mode="count"
                  size={140}
                  onSectorClick={handleSectorClick}
                />
              </div>
              <div className="border-t dark:border-gray-600 pt-3">
                <Link
                  to="/watchlist"
                  className="text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 text-sm font-medium"
                >
                  Manage your watchlist →
                </Link>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* IPO Tracker */}
      <div className="bg-white dark:bg-gray-700 rounded-lg shadow-lg dark:shadow-gray-200/20 p-6 border dark:border-gray-500 mt-8">
        <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
          <div className="flex items-center gap-2">
            <svg className="w-5 h-5 text-primary-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
            </svg>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">IPO Tracker</h2>
          </div>
          <div className="flex items-center gap-3 flex-wrap justify-end">
            <div className="flex rounded-lg overflow-hidden border border-gray-200 dark:border-gray-600">
              {(['CS', 'ETF', 'all'] as const).map((filter) => (
                <button
                  key={filter}
                  onClick={() => setIpoAssetFilter(filter)}
                  title={
                    filter === 'CS' ? 'Share IPOs only'
                      : filter === 'ETF' ? 'New ETF listings only'
                        : 'Share IPOs and ETF listings'
                  }
                  className={`px-3 py-1.5 text-xs font-medium whitespace-nowrap transition-colors ${
                    ipoAssetFilter === filter
                      ? 'bg-primary-600 text-white'
                      : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                  }`}
                >
                  {IPO_ASSET_LABELS[filter]}
                </button>
              ))}
            </div>
            <div className="flex rounded-lg overflow-hidden border border-gray-200 dark:border-gray-600">
              {(['upcoming', 'recently_active', 'pending'] as const).map((tab) => (
                <button
                  key={tab}
                  onClick={() => {
                    setIpoTab(tab);
                    // Price/Change carry different meanings per tab — start each tab
                    // in its own default order rather than inheriting a sort.
                    setIpoSortColumn(null);
                  }}
                  className={`px-4 py-1.5 text-xs font-medium whitespace-nowrap transition-colors ${
                    ipoTab === tab
                      ? 'bg-primary-600 text-white'
                      : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                  }`}
                >
                  {IPO_TAB_LABELS[tab]}
                  {visibleIpos[tab].length > 0 && (
                    <span className={`ml-1.5 px-1.5 py-0.5 rounded-full text-xs ${
                      ipoTab === tab ? 'bg-primary-500 text-white' : 'bg-gray-200 dark:bg-gray-600 text-gray-600 dark:text-gray-300'
                    }`}>
                      {visibleIpos[tab].length}
                    </span>
                  )}
                </button>
              ))}
            </div>
          </div>
        </div>

        {ipoLoading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
            <span className="ml-3 text-gray-500 dark:text-gray-400">Loading IPOs...</span>
          </div>
        ) : visibleIpos[ipoTab].length === 0 ? (
          <div className="text-center py-10">
            <svg className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
            </svg>
            <p className="text-gray-500 dark:text-gray-400">
              No {IPO_TAB_LABELS[ipoTab].toLowerCase()} {IPO_ASSET_NOUNS[ipoAssetFilter]} found
            </p>
            {ipoAssetFilter !== 'all' && ipoData[ipoTab].length > 0 && (
              <button
                onClick={() => setIpoAssetFilter('all')}
                className="mt-2 text-sm text-primary-600 dark:text-primary-400 hover:underline"
              >
                Show all {ipoData[ipoTab].length} {IPO_TAB_LABELS[ipoTab].toLowerCase()} listings
              </button>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-600">
              <thead className="bg-gray-800 dark:bg-gray-900">
                <tr>
                  {([
                    { key: 'ticker', label: 'Ticker', align: 'text-left' },
                    { key: 'company', label: 'Company', align: 'text-left' },
                    { key: 'date', label: ipoTab === 'upcoming' ? 'Expected Date' : 'Listed Date', align: 'text-left' },
                    { key: 'price', label: ipoTab === 'recently_active' ? 'Price' : 'Price Range', align: 'text-right' },
                    { key: 'change', label: ipoTab === 'recently_active' ? 'Change' : 'Offer Size', align: 'text-right' },
                  ] as { key: IPOSortColumn; label: string; align: string }[]).map(col => (
                    <th
                      key={col.key}
                      onClick={() => handleIpoSort(col.key)}
                      className={`px-4 py-3 ${col.align} text-xs font-medium text-gray-300 uppercase tracking-wider cursor-pointer hover:text-white select-none transition-colors`}
                    >
                      <span className="inline-flex items-center gap-1">
                        {col.label}
                        {ipoSortColumn === col.key ? (
                          <span className="text-primary-400">{ipoSortDirection === 'asc' ? '▲' : '▼'}</span>
                        ) : (
                          <span className="text-gray-500">⇅</span>
                        )}
                      </span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-600">
                {sortedIpos.map((ipo, idx) => (
                  <tr key={`${ipo.ticker}-${idx}`} className="hover:bg-gray-50 dark:hover:bg-gray-600/50 transition-colors">
                    <td className="px-4 py-3 whitespace-nowrap">
                      {ipo.ticker && ipo.ticker !== 'N/A' ? (
                        <button
                          onClick={() => openIpoModal(ipo)}
                          className="text-sm font-semibold text-primary-600 dark:text-primary-400 hover:text-primary-800 dark:hover:text-primary-300 hover:underline cursor-pointer"
                        >
                          {ipo.ticker}
                        </button>
                      ) : (
                        <span className="text-sm text-gray-400 dark:text-gray-500">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-sm text-gray-900 dark:text-white font-medium truncate max-w-xs">{ipo.issuer_name}</div>
                      <div className="flex items-center gap-2">
                        {/* Only under "All" — beside a filtered list the badge
                            would repeat what the filter already said. */}
                        {ipoAssetFilter === 'all' && ipo.asset_type === 'ETF' && (
                          <span className="text-xs px-1.5 py-0.5 rounded bg-gray-200 dark:bg-gray-600 text-gray-600 dark:text-gray-300">
                            ETF
                          </span>
                        )}
                        {ipo.security_type && (
                          <span className="text-xs text-gray-500 dark:text-gray-400">{ipo.security_type}</span>
                        )}
                        {ipo.announced_date && (
                          <span className="text-xs text-gray-400 dark:text-gray-500">
                            Announced {new Date(ipo.announced_date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      {ipo.listing_date ? (
                        <span className="text-sm text-gray-900 dark:text-white">
                          {new Date(ipo.listing_date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                        </span>
                      ) : (
                        <span className="text-sm text-gray-400 dark:text-gray-500">TBD</span>
                      )}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-right">
                      {ipoTab === 'recently_active' ? (
                        ipo.current_price ? (
                          <span className="text-sm font-medium text-gray-900 dark:text-white">
                            ${ipo.current_price.toFixed(2)}
                          </span>
                        ) : (
                          <span className="text-sm text-gray-400 dark:text-gray-500">—</span>
                        )
                      ) : ipo.lowest_offer_price && ipo.highest_offer_price ? (
                        <span className="text-sm text-gray-600 dark:text-gray-400">
                          ${ipo.lowest_offer_price.toFixed(2)} – ${ipo.highest_offer_price.toFixed(2)}
                        </span>
                      ) : (
                        <span className="text-sm text-gray-400 dark:text-gray-500">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-right">
                      {ipoTab === 'recently_active' ? (
                        ipo.change_percent !== null && ipo.change_percent !== undefined ? (
                          <span className={`text-sm font-medium ${ipo.change_percent >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                            {ipo.change_percent >= 0 ? '+' : ''}{ipo.change_percent.toFixed(2)}%
                          </span>
                        ) : (
                          <span className="text-sm text-gray-400 dark:text-gray-500">—</span>
                        )
                      ) : ipo.total_offer_size ? (
                        <span className="text-sm text-gray-600 dark:text-gray-400">
                          ${(ipo.total_offer_size / 1e6).toFixed(1)}M
                        </span>
                      ) : (
                        <span className="text-sm text-gray-400 dark:text-gray-500">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Upgrade CTA (for Beginner users) */}
      {user?.subscription_tier === 'beginner' && (
        <div className="mt-8 bg-gradient-to-r from-primary-600 to-primary-700 dark:from-primary-700 dark:to-primary-800 rounded-lg shadow-lg p-8 text-center">
          <h3 className="text-2xl font-bold text-white mb-2">
            Upgrade to {CASUAL.name} to unlock more features
          </h3>
          <p className="text-primary-100 dark:text-primary-200 mb-6">
            {CASUAL_SUMMARY.features.slice(0, 4).join(' · ')}. Only {CASUAL_SUMMARY.price}.
          </p>
          <Link
            to="/payment?tier=casual"
            className="inline-block bg-white text-primary-600 px-8 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors"
          >
            Upgrade to {CASUAL.name}
          </Link>
        </div>
      )}

      {/* ── Market Overview Modal ─────────────────────────────────── */}
      {marketModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setMarketModal(null)} />
          <div className="relative bg-white dark:bg-gray-800 rounded-xl shadow-2xl max-w-lg w-full max-h-[80vh] overflow-hidden border dark:border-gray-600">
            {/* Modal Header */}
            <div className={`px-6 py-4 border-b dark:border-gray-700 flex items-center justify-between ${
              marketModal === 'treasury' ? 'bg-blue-50 dark:bg-blue-900/20' :
              marketModal === 'commodities' ? 'bg-yellow-50 dark:bg-yellow-900/20' :
              marketModal === 'indexes' ? 'bg-green-50 dark:bg-green-900/20' :
              'bg-orange-50 dark:bg-orange-900/20'
            }`}>
              <div className="flex items-center gap-2">
                {marketModal === 'treasury' && (
                  <div className="bg-blue-100 dark:bg-blue-900/30 rounded-lg p-1.5">
                    <svg className="w-5 h-5 text-blue-600 dark:text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                    </svg>
                  </div>
                )}
                {marketModal === 'commodities' && (
                  <div className="bg-yellow-100 dark:bg-yellow-900/30 rounded-lg p-1.5">
                    <svg className="w-5 h-5 text-yellow-600 dark:text-yellow-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                    </svg>
                  </div>
                )}
                {marketModal === 'indexes' && (
                  <div className="bg-green-100 dark:bg-green-900/30 rounded-lg p-1.5">
                    <svg className="w-5 h-5 text-green-600 dark:text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                    </svg>
                  </div>
                )}
                {marketModal === 'crypto' && (
                  <div className="bg-orange-100 dark:bg-orange-900/30 rounded-lg p-1.5">
                    <svg className="w-5 h-5 text-orange-600 dark:text-orange-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                )}
                <h2 className="text-lg font-bold text-gray-900 dark:text-white">
                  {marketModal === 'treasury' ? 'Treasury Rates' :
                   marketModal === 'commodities' ? 'Commodities' :
                   marketModal === 'indexes' ? 'Market Indexes' :
                   'Cryptocurrency'}
                </h2>
              </div>
              <button onClick={() => setMarketModal(null)} className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors">
                <svg className="w-5 h-5 text-gray-500 dark:text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Modal Body */}
            <div className="overflow-y-auto max-h-[calc(80vh-4rem)]">
              {(() => {
                const data = marketModal === 'treasury' ? treasuryRates :
                             marketModal === 'commodities' ? commodities :
                             marketModal === 'indexes' ? indexes :
                             crypto;
                const isTreasury = marketModal === 'treasury';
                const isIndex = marketModal === 'indexes';

                if (marketDataLoading) {
                  return (
                    <div className="flex items-center justify-center py-12">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
                      <span className="ml-3 text-gray-500 dark:text-gray-400">Loading...</span>
                    </div>
                  );
                }

                if (data.length === 0) {
                  return (
                    <div className="text-center py-12">
                      <p className="text-gray-500 dark:text-gray-400">No data available</p>
                    </div>
                  );
                }

                return (
                  <table className="min-w-full">
                    <thead className="bg-gray-50 dark:bg-gray-900 sticky top-0">
                      <tr>
                        <th className="px-5 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Name</th>
                        <th className="px-5 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                          {isTreasury ? 'Yield' : isIndex ? 'Level' : 'Price'}
                        </th>
                        <th className="px-5 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Change</th>
                        <th className="px-5 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">% Change</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                      {data.map((item) => (
                        <tr key={item.name} className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                          <td className="px-5 py-3 text-sm font-medium text-gray-900 dark:text-white">{item.name}</td>
                          <td className="px-5 py-3 text-sm text-right font-semibold text-gray-900 dark:text-white">
                            {item.value != null
                              ? isTreasury
                                ? `${item.value.toFixed(2)}%`
                                : isIndex
                                ? item.value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                                : `$${item.value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                              : '—'}
                          </td>
                          <td className={`px-5 py-3 text-sm text-right font-medium ${
                            (item.change ?? 0) >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'
                          }`}>
                            {item.change != null ? `${item.change >= 0 ? '+' : ''}${isTreasury ? item.change.toFixed(3) : item.change.toFixed(2)}` : '—'}
                          </td>
                          <td className={`px-5 py-3 text-sm text-right font-medium ${
                            (item.changePercent ?? 0) >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'
                          }`}>
                            {item.changePercent != null ? `${item.changePercent >= 0 ? '+' : ''}${item.changePercent.toFixed(2)}%` : '—'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                );
              })()}
            </div>
          </div>
        </div>
      )}

      {/* ── IPO Detail Modal ─────────────────────────────────────── */}
      {ipoModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          {/* Backdrop */}
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={closeIpoModal} />

          {/* Modal */}
          <div className="relative bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-2xl max-h-[85vh] overflow-y-auto border border-gray-200 dark:border-gray-600">
            {/* Header */}
            <div className="sticky top-0 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-600 px-6 py-4 flex items-center justify-between z-10">
              <div>
                <h2 className="text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
                  <span className="text-primary-600 dark:text-primary-400">{ipoModalTicker}</span>
                  {ipoModalCompany?.name && (
                    <span className="text-base font-normal text-gray-500 dark:text-gray-400">· {ipoModalCompany.name}</span>
                  )}
                </h2>
                {ipoModalIpo?.security_type && (
                  <span className="text-xs text-gray-500 dark:text-gray-400">{ipoModalIpo.security_type}</span>
                )}
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={handleIpoAddToWatchlist}
                  disabled={ipoWatchlistStatus === 'adding' || ipoWatchlistStatus === 'added' || ipoWatchlistStatus === 'already'}
                  className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors flex items-center gap-1.5 ${
                    ipoWatchlistStatus === 'added'
                      ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300'
                      : ipoWatchlistStatus === 'already'
                      ? 'bg-gray-100 dark:bg-gray-600 text-gray-500 dark:text-gray-400 cursor-default'
                      : ipoWatchlistStatus === 'error'
                      ? 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 hover:bg-red-200 dark:hover:bg-red-900/50'
                      : 'bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 hover:bg-primary-200 dark:hover:bg-primary-900/50'
                  }`}
                >
                  {ipoWatchlistStatus === 'adding' ? (
                    <>
                      <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-current"></div>
                      Adding...
                    </>
                  ) : ipoWatchlistStatus === 'added' ? (
                    <>
                      <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                      Added to Watchlist
                    </>
                  ) : ipoWatchlistStatus === 'already' ? (
                    <>
                      <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                      On Watchlist
                    </>
                  ) : ipoWatchlistStatus === 'error' ? (
                    <>⚠ Retry</>
                  ) : (
                    <>
                      <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" /></svg>
                      Add to Watchlist
                    </>
                  )}
                </button>
                <button onClick={closeIpoModal} className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors">
                  <svg className="w-5 h-5 text-gray-500 dark:text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            {ipoModalLoading ? (
              <div className="flex items-center justify-center py-16">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
                <span className="ml-3 text-gray-500 dark:text-gray-400">Loading data for {ipoModalTicker}...</span>
              </div>
            ) : (
              <div className="px-6 py-4 space-y-5">

                {/* ── Price & Quote Row ───────────────────────────── */}
                {ipoModalQuote ? (
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                    <div>
                      <div className="text-xs text-gray-500 dark:text-gray-400 uppercase">Current Price</div>
                      <div className="text-xl font-bold text-gray-900 dark:text-white">
                        ${ipoModalQuote.price?.toFixed(2) ?? '—'}
                      </div>
                    </div>
                    <div>
                      <div className="text-xs text-gray-500 dark:text-gray-400 uppercase">Change</div>
                      <div className={`text-lg font-semibold ${(ipoModalQuote.change_percent ?? 0) >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                        {(ipoModalQuote.change_percent ?? 0) >= 0 ? '+' : ''}{ipoModalQuote.change_percent?.toFixed(2) ?? '—'}%
                      </div>
                    </div>
                    <div>
                      <div className="text-xs text-gray-500 dark:text-gray-400 uppercase">Volume</div>
                      <div className="text-sm font-medium text-gray-900 dark:text-white">
                        {ipoModalQuote.volume ? (ipoModalQuote.volume >= 1e6 ? `${(ipoModalQuote.volume / 1e6).toFixed(1)}M` : ipoModalQuote.volume.toLocaleString()) : '—'}
                      </div>
                    </div>
                    <div>
                      <div className="text-xs text-gray-500 dark:text-gray-400 uppercase">Day Range</div>
                      <div className="text-sm font-medium text-gray-900 dark:text-white">
                        {ipoModalQuote.low?.toFixed(2) ?? '—'} – {ipoModalQuote.high?.toFixed(2) ?? '—'}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-700 rounded-lg p-3">
                    <p className="text-sm text-yellow-700 dark:text-yellow-300">
                      No live quote available — this ticker may not be actively trading yet.
                    </p>
                  </div>
                )}

                {/* ── IPO Details ─────────────────────────────────── */}
                {ipoModalIpo && (
                  <div>
                    <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider mb-2">IPO Details</h3>
                    <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4 grid grid-cols-2 sm:grid-cols-3 gap-3 text-sm">
                      <div>
                        <span className="text-gray-500 dark:text-gray-400">Listing Date</span>
                        <div className="font-medium text-gray-900 dark:text-white">
                          {ipoModalIpo.listing_date
                            ? new Date(ipoModalIpo.listing_date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                            : 'TBD'}
                        </div>
                      </div>
                      {ipoModalIpo.announced_date && (
                        <div>
                          <span className="text-gray-500 dark:text-gray-400">Announced</span>
                          <div className="font-medium text-gray-900 dark:text-white">
                            {new Date(ipoModalIpo.announced_date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                          </div>
                        </div>
                      )}
                      <div>
                        <span className="text-gray-500 dark:text-gray-400">Issue Price</span>
                        <div className="font-medium text-gray-900 dark:text-white">
                          {ipoModalIpo.final_issue_price ? `$${ipoModalIpo.final_issue_price.toFixed(2)}` : '—'}
                        </div>
                      </div>
                      <div>
                        <span className="text-gray-500 dark:text-gray-400">Price Range</span>
                        <div className="font-medium text-gray-900 dark:text-white">
                          {ipoModalIpo.lowest_offer_price && ipoModalIpo.highest_offer_price
                            ? `$${ipoModalIpo.lowest_offer_price.toFixed(2)} – $${ipoModalIpo.highest_offer_price.toFixed(2)}`
                            : '—'}
                        </div>
                      </div>
                      <div>
                        <span className="text-gray-500 dark:text-gray-400">Offer Size</span>
                        <div className="font-medium text-gray-900 dark:text-white">
                          {ipoModalIpo.total_offer_size ? `$${(ipoModalIpo.total_offer_size / 1e6).toFixed(1)}M` : '—'}
                        </div>
                      </div>
                      <div>
                        <span className="text-gray-500 dark:text-gray-400">Exchange</span>
                        <div className="font-medium text-gray-900 dark:text-white">
                          {ipoModalIpo.primary_exchange || '—'}
                        </div>
                      </div>
                      {ipoModalIpo.max_shares_offered && (
                        <div>
                          <span className="text-gray-500 dark:text-gray-400">Shares Offered</span>
                          <div className="font-medium text-gray-900 dark:text-white">
                            {ipoModalIpo.min_shares_offered && ipoModalIpo.min_shares_offered !== ipoModalIpo.max_shares_offered
                              ? `${(ipoModalIpo.min_shares_offered / 1e6).toFixed(1)}M – ${(ipoModalIpo.max_shares_offered / 1e6).toFixed(1)}M`
                              : `${(ipoModalIpo.max_shares_offered / 1e6).toFixed(1)}M`}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* ── Company Description ─────────────────────────── */}
                {ipoModalCompany?.description && (
                  <div>
                    <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider mb-2">About</h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed line-clamp-4">
                      {ipoModalCompany.description}
                    </p>
                    {ipoModalCompany.sector && (
                      <div className="mt-2 flex items-center gap-2 flex-wrap">
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300">
                          {ipoModalCompany.sector}
                        </span>
                        {ipoModalCompany.industry && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 dark:bg-gray-600 text-gray-700 dark:text-gray-300">
                            {ipoModalCompany.industry}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {/* ── News Articles ───────────────────────────────── */}
                <div>
                  <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider mb-2">Recent News</h3>
                  {ipoModalNews.length > 0 ? (
                    <div className="space-y-2">
                      {ipoModalNews.map((article: any, i: number) => (
                        <a
                          key={i}
                          href={article.article_url || article.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="block bg-gray-50 dark:bg-gray-700/50 hover:bg-gray-100 dark:hover:bg-gray-600/50 rounded-lg p-3 transition-colors"
                        >
                          <div className="text-sm font-medium text-gray-900 dark:text-white line-clamp-2">{article.title}</div>
                          <div className="flex items-center gap-2 mt-1">
                            <span className="text-xs text-gray-500 dark:text-gray-400">{article.publisher || article.source}</span>
                            {article.published_utc && (
                              <span className="text-xs text-gray-400 dark:text-gray-500">
                                {new Date(article.published_utc).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                              </span>
                            )}
                          </div>
                        </a>
                      ))}
                    </div>
                  ) : (
                    <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4 text-center">
                      <p className="text-sm text-gray-500 dark:text-gray-400">No news articles found for {ipoModalTicker}.</p>
                      <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">News coverage typically appears closer to or after the listing date.</p>
                    </div>
                  )}
                </div>

              </div>
            )}
          </div>
        </div>
      )}
    </div>
    <BackToTop />
  </div>
);
};

export default Dashboard;